import {
  messageKind,
  type DeliverTakeRequest,
  type DeliverTakeResponse,
  type MessagePortLike,
} from "./protocol.js";
import type { DeliveryRequest, DeliveryResult } from "./delivery.js";

/**
 * The one thing the core worker asks the page for.
 *
 * Everything else on this port runs the other way — the page asks and the worker
 * answers, because the worker is where the state is. Delivery is the exception
 * and it is forced rather than chosen: the take's bytes are in origin-private
 * storage because only a worker can write to it synchronously, and both ways of
 * getting a file out of a browser are window APIs. Neither half can do the whole
 * job, so at exactly one moment the worker asks.
 *
 * Deliberately not a contract channel. The desktop has no counterpart — main
 * writes the file itself, with no dialog and no renderer involved — so a channel
 * here would be a change to a contract whose whole value is that it did not
 * change. Same reasoning as the two settings messages beside it.
 */

/**
 * The worker's end: ask the page to deliver a take, and wait for the answer.
 *
 * Waiting is the point. `RecordingController` discards a session as soon as its
 * bytes are reported safe somewhere else, so an answer that arrived after the
 * fact would be an answer about footage that had already been deleted.
 */
export function deliverThroughPage(
  port: MessagePortLike,
): (request: DeliveryRequest) => Promise<DeliveryResult> {
  const waiting = new Map<number, (result: DeliveryResult) => void>();
  let nextId = 1;

  port.addEventListener("message", (event) => {
    if (messageKind(event.data) !== "deliver-take-result") return;
    const response = event.data as DeliverTakeResponse;
    const settle = waiting.get(response.id);
    if (!settle) return;
    waiting.delete(response.id);
    settle(response.result);
  });
  port.start?.();

  return (request) =>
    new Promise<DeliveryResult>((resolve) => {
      const id = nextId++;
      waiting.set(id, resolve);
      port.postMessage({
        kind: "deliver-take",
        id,
        request,
      } satisfies DeliverTakeRequest);
    });
}

/**
 * The page's end: do what is asked and say what happened.
 *
 * Never rejects out into the port. A page that threw here would leave the worker
 * waiting on a promise that never settles, which is a transport stuck in
 * `stopping` — the one state FR-49 says the user must never be left guessing in.
 */
export function attachTakeDelivery(
  port: MessagePortLike,
  deliver: (request: DeliveryRequest) => Promise<DeliveryResult>,
): void {
  port.addEventListener("message", (event) => {
    if (messageKind(event.data) !== "deliver-take") return;
    const { id, request } = event.data as DeliverTakeRequest;
    void (async () => {
      let result: DeliveryResult;
      try {
        result = await deliver(request);
      } catch (error: unknown) {
        result = {
          ok: false,
          message: `Couldn't save the take: ${
            error instanceof Error ? error.message : String(error)
          }`,
        };
      }
      port.postMessage({
        kind: "deliver-take-result",
        id,
        result,
      } satisfies DeliverTakeResponse);
    })();
  });
  port.start?.();
}
